import { useMemo } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { FileText, MessageSquare, Phone, Sparkles, Zap } from 'lucide-react'

import { authFetch } from '@/lib/api'
import { formatRelative } from '@/lib/dates'
import { formatMoney } from '@/lib/format'
import { useAssistantName } from '@/lib/use-assistant-name'

/**
 * The shop's chronological log (DESIGN.md §5 More → Activity): a
 * client-side merge of the manual lane (`GET /api/activity` — estimates,
 * calls, messages) and the assistant lane (`GET /api/eric/history`),
 * same merge pattern CustomerDetail's timeline uses.
 *
 * Lane attribution: `/api/eric/history` derives a `lane` marker from
 * the persisted cascade audit row (`reasoning.source === 'cascade'`).
 * Cascade rows render as quick actions — bolt badge (DESIGN.md §2.1),
 * title from `final_reply` (never the machine `request_text`). Brain
 * rows keep the mechanic's words as the title. Unmarked historical
 * rows carry `lane:'brain'` and render brain-style (no backfill).
 */

// Only the fields this page consumes — the wire rows carry more
// (status, channel, guardrail_decision, …); structural typing keeps
// the wider server responses assignable.
type ManualRow =
  | {
      kind: 'estimate'
      id: string
      created_at: string
      title: string
      subtitle: string | null
      total: number | null
    }
  | {
      kind: 'call'
      id: string
      created_at: string
      title: string
      subtitle: string | null
    }
  | {
      kind: 'message'
      id: string
      created_at: string
      title: string
      subtitle: string | null
      direction: string
    }

type AssistantEntry = {
  id: string
  request_text: string | null
  final_reply: string | null
  created_at: string
  // Optional so rows from a pre-marker response (or cache) degrade to
  // the brain-style default rather than crashing.
  lane?: 'cascade' | 'brain'
}

type LogRow = {
  key: string
  kind: 'estimate' | 'call' | 'message' | 'assistant' | 'quick_action'
  title: string
  sub: string | null
  at: string
}

const KIND_ICONS = {
  estimate: FileText,
  call: Phone,
  message: MessageSquare,
  assistant: Sparkles,
  quick_action: Zap,
} as const

// Shared by the skeleton and the loaded list so the two states can't
// drift apart on spacing or borders.
const LIST_CLASS =
  'mt-4 overflow-hidden rounded-[14px] border border-border bg-background'
const ROW_CLASS =
  'flex min-h-[60px] items-center gap-3 border-t border-border px-3.5 py-3 first:border-t-0'

function mergeRows(
  manual: ManualRow[],
  assistant: AssistantEntry[],
  t: TFunction,
): LogRow[] {
  const rows: LogRow[] = []

  for (const m of manual) {
    let sub = m.subtitle
    if (m.kind === 'message') {
      // Same direction arrows as CustomerDetail's timeline — inbound =
      // from the customer (→ us), outbound = from the shop (← us).
      const arrow = m.direction === 'inbound' ? '→' : '←'
      sub = m.subtitle ? `${arrow} ${m.subtitle}` : null
    } else if (m.kind === 'estimate' && m.total !== null) {
      sub = [m.subtitle, formatMoney(m.total)].filter(Boolean).join(' · ')
    }
    rows.push({
      key: `${m.kind}-${m.id}`,
      kind: m.kind,
      title: m.title,
      sub,
      at: m.created_at,
    })
  }

  for (const a of assistant) {
    if (a.lane === 'cascade') {
      // Quick-action (fast-lane) row: the stored `request_text` is
      // machine framing (ISO timestamps, internal ids) — never show it.
      // `final_reply` is the human sentence the cascade wrote ("Booked
      // Nolan."); the kind label is the last-resort fallback.
      rows.push({
        key: `assistant-${a.id}`,
        kind: 'quick_action',
        title: a.final_reply?.trim() || t('kind.quick_action'),
        sub: null,
        at: a.created_at,
      })
      continue
    }
    rows.push({
      key: `assistant-${a.id}`,
      kind: 'assistant',
      title: a.request_text?.trim() || t('assistant.request_fallback'),
      sub: a.final_reply?.trim() || t('assistant.no_reply'),
      at: a.created_at,
    })
  }

  rows.sort((x, y) => {
    const xt = new Date(x.at).getTime()
    const yt = new Date(y.at).getTime()
    const xs = Number.isFinite(xt) ? xt : 0
    const ys = Number.isFinite(yt) ? yt : 0
    if (xs !== ys) return ys - xs
    return x.key.localeCompare(y.key)
  })
  return rows
}

export function Activity() {
  const { t } = useTranslation('activity')
  const { getToken, isLoaded } = useAuth()
  const assistantName = useAssistantName()

  const manualQuery = useQuery({
    queryKey: ['activity', 'log'],
    enabled: isLoaded,
    queryFn: () => authFetch<ManualRow[]>('/api/activity?limit=50', getToken),
  })
  const historyQuery = useQuery({
    queryKey: ['eric-history', 'activity-log'],
    enabled: isLoaded,
    queryFn: () =>
      authFetch<{ entries: AssistantEntry[] }>(
        '/api/eric/history?limit=50',
        getToken,
      ),
  })

  const rows = useMemo(
    () =>
      mergeRows(manualQuery.data ?? [], historyQuery.data?.entries ?? [], t),
    [manualQuery.data, historyQuery.data, t],
  )

  const loading = manualQuery.isLoading || historyQuery.isLoading
  const bothFailed = manualQuery.isError && historyQuery.isError
  const partialError =
    !bothFailed && (manualQuery.isError || historyQuery.isError)

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="text-[22px] font-bold tracking-[-0.015em] text-foreground [text-wrap:balance]">
        {t('title')}
      </h1>

      {loading ? (
        <div className={LIST_CLASS}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={ROW_CLASS}>
              <div className="size-4 shrink-0 animate-pulse rounded bg-muted/60 motion-reduce:animate-none" />
              <div className="min-w-0 flex-1 space-y-1.5">
                <div className="h-3.5 w-2/5 animate-pulse rounded bg-muted/60 motion-reduce:animate-none" />
                <div className="h-3 w-3/5 animate-pulse rounded bg-muted/40 motion-reduce:animate-none" />
              </div>
            </div>
          ))}
        </div>
      ) : bothFailed ? (
        <p className="mt-4 rounded-[14px] border border-border bg-background px-3.5 py-8 text-center text-[13px] text-muted-foreground">
          {t('error')}
        </p>
      ) : rows.length === 0 ? (
        <p className="mt-4 rounded-[14px] border border-border bg-background px-3.5 py-8 text-center text-[13px] text-muted-foreground">
          {t('empty', { name: assistantName })}
        </p>
      ) : (
        <>
          {partialError ? (
            <p className="mt-4 text-[13px] text-muted-foreground">
              {t('partial_error')}
            </p>
          ) : null}
          <ul className={LIST_CLASS}>
            {rows.map((row) => {
              const Icon = KIND_ICONS[row.kind]
              return (
                <li key={row.key} className={ROW_CLASS}>
                  <Icon
                    aria-hidden="true"
                    className="size-4 shrink-0 text-ink-3"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="sr-only">{t(`kind.${row.kind}`)}: </span>
                    <span className="block truncate text-[15px] font-semibold text-foreground">
                      {row.title}
                    </span>
                    {row.sub ? (
                      <span className="block truncate text-[13px] text-muted-foreground">
                        {row.sub}
                      </span>
                    ) : null}
                  </span>
                  <time
                    dateTime={row.at}
                    className="shrink-0 whitespace-nowrap text-[12px] text-muted-foreground"
                  >
                    {formatRelative(row.at)}
                  </time>
                </li>
              )
            })}
          </ul>
        </>
      )}
    </div>
  )
}
